'use client'

import { CodeBlock } from '@brew/code-block'
import { Button } from '@brew/ui/button'
import { cn } from '@brew/ui/cn'
import { ArrowUpRight, Plug } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { type ReactNode, useState } from 'react'
import { McpClientLogo } from '@/components/settings/mcp/mcp-client-logo'
import { AnimatedCopyIcon } from '@/components/ui/animated-copy-icon'
import {
  getMcpClient,
  MCP_CLIENTS,
  type McpClientId,
  type McpSetupArtifact,
} from '@/lib/mcp/clients'
import { buildCursorInstallUrl } from '@/lib/mcp/handoff'
import { appToast } from '@/lib/shared/toast'
import { useMcpConnectionUrl } from './mcp-connection-url'

const MCP_MARKETING_URL = '/mcp'
const MCP_DOCS_URL =
  'https://docs.brew.new/api-reference/mcp/connect-your-client'
const DEFAULT_CLIENT: McpClientId = 'claude-desktop'

/**
 * A recipe's copyable artifact. Its label and its copy button's name follow
 * its kind, each its own message, never a label glued into "Copy …".
 */
function CopyField({
  artifact,
  url,
}: {
  artifact: McpSetupArtifact
  url: string
}) {
  const t = useTranslations('settings')
  const tCommon = useTranslations('common')
  const value = artifact.build(url)
  let label = t('mcp.connect.serverUrl')
  let copyLabel = t('mcp.connect.copy.serverUrl')
  if (artifact.kind === 'config') {
    label = artifact.filename
    copyLabel = t('mcp.connect.copy.configFile', { path: artifact.filename })
  }
  if (artifact.kind === 'command') {
    label = t('mcp.connect.runInTerminal')
    copyLabel = t('mcp.connect.copy.command')
  }
  const [isCopied, setIsCopied] = useState(false)
  const copy = () => {
    void navigator.clipboard.writeText(value).then(() => {
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
      appToast.success(tCommon('actions.copied'))
    })
  }
  const handleBlockCopy = () => {
    appToast.success(tCommon('actions.copied'))
  }

  // Configuration and commands carry explicit syntax; only URLs use the compact field.
  if (artifact.kind !== 'url') {
    return (
      <div className="flex flex-col gap-1.5">
        <span className="font-medium text-caption text-muted-foreground uppercase">
          {label}
        </span>
        <CodeBlock
          code={value}
          copyLabel={copyLabel}
          filename={
            artifact.kind === 'config'
              ? artifact.filename
              : t('mcp.connect.terminal')
          }
          language={artifact.language}
          onCopy={handleBlockCopy}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-medium text-caption text-muted-foreground uppercase">
        {label}
      </span>
      <div className="flex items-start gap-2">
        <code className="min-w-0 flex-1 overflow-x-auto whitespace-pre rounded-lg border border-border bg-background px-2.5 py-2 font-mono text-xs">
          {value}
        </code>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-9 shrink-0"
          onClick={copy}
          aria-label={copyLabel}
        >
          <AnimatedCopyIcon copied={isCopied} className="size-4" />
        </Button>
      </div>
    </div>
  )
}

/** Only the recipe's bold controls and inline code are rendered as markup. */
function SetupText({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\*\*.*?\*\*|`.*?`)/g).map((part, index) => {
        let content: ReactNode = part
        if (part.startsWith('**')) {
          content = <strong>{part.slice(2, -2)}</strong>
        } else if (part.startsWith('`')) {
          content = <code className="font-mono">{part.slice(1, -1)}</code>
        }
        // biome-ignore lint/suspicious/noArrayIndexKey: tokens are static recipe text
        return <span key={index}>{content}</span>
      })}
    </>
  )
}

/** Complete recipes are shared by settings, marketing, and onboarding. */
export function ClientSetup({
  clientId,
  url,
  header = false,
}: {
  clientId: McpClientId
  url: string
  header?: boolean
}) {
  const t = useTranslations('settings')
  const client = getMcpClient(clientId)
  return (
    <div className="flex flex-col gap-3">
      {header ? (
        <div className="flex items-center gap-3">
          <McpClientLogo
            domain={client.logoDomain}
            name={client.name}
            className="size-9 rounded-lg"
          />
          <div className="flex flex-col">
            <span className="font-medium text-foreground text-sm">
              {client.name}
            </span>
            <span className="text-muted-foreground text-xs">
              {client.tagline}
            </span>
          </div>
        </div>
      ) : (
        <span className="font-medium text-foreground text-sm">
          {client.tagline}
        </span>
      )}
      {client.recipes.map((recipe) => (
        <section
          key={recipe.title}
          aria-label={recipe.title}
          className="flex flex-col gap-3"
        >
          {client.recipes.length > 1 ? (
            <p className="font-medium text-sm">{recipe.title}</p>
          ) : null}
          <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-muted-foreground text-sm leading-relaxed">
            {recipe.steps.map((step) => (
              <li key={step}>
                <SetupText text={step} />
              </li>
            ))}
          </ol>
          {client.id === 'cursor' ? (
            <a
              href={buildCursorInstallUrl(url)}
              className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1.5 font-medium text-xs transition-colors hover:border-primary/40"
            >
              <McpClientLogo
                domain={client.logoDomain}
                name={client.name}
                className="size-3.5 rounded"
              />
              {t('mcp.connect.addToCursor')}
              <ArrowUpRight className="size-3" />
            </a>
          ) : null}
          <CopyField artifact={recipe.artifact} url={url} />
          <p className="text-muted-foreground text-sm leading-relaxed">
            <SetupText text={recipe.authentication} />
          </p>
          <p className="text-muted-foreground text-sm leading-relaxed">
            <SetupText text={recipe.verification} />
          </p>
          {recipe.note ? (
            <p className="rounded-lg border border-border border-dashed bg-background/60 px-2.5 py-2 text-muted-foreground text-xs leading-relaxed">
              <SetupText text={recipe.note} />
            </p>
          ) : null}
        </section>
      ))}
      {client.caveat ? (
        <p className="text-muted-foreground text-xs leading-relaxed">
          <SetupText text={client.caveat} />
        </p>
      ) : null}
    </div>
  )
}

/**
 * "Set up your agent" and the client chips: pick an agent to show its
 * setup. Controlled, so a host can draw the setup elsewhere (the home
 * page's MCP visual lifts it into its own card). `chipsClassName` lays the
 * chips out (they wrap by default).
 */
export function McpClientPicker({
  clientId,
  onSelect,
  chipsClassName,
}: {
  clientId: McpClientId
  onSelect: (clientId: McpClientId) => void
  chipsClassName?: string
}) {
  const t = useTranslations('settings')
  return (
    <>
      <span className="font-medium text-caption text-muted-foreground uppercase">
        {t('mcp.connect.setUpAgent')}
      </span>

      <div className={cn('flex flex-wrap gap-1.5', chipsClassName)}>
        {MCP_CLIENTS.map((client) => {
          const isActive = client.id === clientId
          return (
            <button
              key={client.id}
              type="button"
              onClick={() => onSelect(client.id)}
              aria-pressed={isActive}
              className={cn(
                'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 font-medium text-xs transition-colors',
                isActive
                  ? 'bg-foreground/10 text-foreground'
                  : 'text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground'
              )}
            >
              <McpClientLogo
                domain={client.logoDomain}
                name={client.name}
                className="size-4"
              />
              {client.name}
            </button>
          )
        })}
      </div>
    </>
  )
}

export type McpConnectPanelProps = {
  className?: string
  /**
   * `card` (default) wraps the panel in its own bordered surface with a Plug
   * header — for standalone use like the onboarding wizard's API/MCP step.
   * `bare` drops the surface and header so it sits inside a section that already
   * supplies its own title + description (the Settings → MCP tab).
   * `grid` renders a responsive two-pane layout — a client picker grid on the
   * left, the selected client's setup on the right — for the marketing showcase
   * and the onboarding step-3 visualization.
   */
  variant?: 'card' | 'bare' | 'grid'
  /**
   * Drop the standalone "Connection URL" field at the top. Each client's setup
   * already surfaces its own copyable URL/config, so a host that only needs the
   * per-client instructions (the send dialog's MCP handoff) can hide the
   * duplicate. Only applies to `card`/`bare`.
   */
  hideConnectionUrl?: boolean
  /**
   * The MCP URL to show instead of the rendering deployment's own (localhost
   * or a preview host): the marketing showcase always shows production's.
   */
  url?: string
}

/** The two-pane client-picker layout (marketing showcase + onboarding step 3). */
function GridSetup({ url, className }: { url: string; className?: string }) {
  const [clientId, setClientId] = useState<McpClientId>(DEFAULT_CLIENT)
  return (
    <div
      className={cn(
        // `minmax(0,…)` + `min-w-0` on both panes keep the split a FIXED ratio:
        // without them the tracks default to `minmax(auto,…)`, so the right
        // pane's widest copyable line (e.g. the long `claude mcp add …` command)
        // would force its track wider and shrink the client picker — the layout
        // would jump every time you selected a different client.
        'grid gap-4 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] md:gap-6',
        className
      )}
    >
      {/* Left: client picker grid */}
      <div className="grid min-w-0 grid-cols-2 gap-2.5 self-start">
        {MCP_CLIENTS.map((client) => {
          const isActive = client.id === clientId
          return (
            <button
              key={client.id}
              type="button"
              onClick={() => setClientId(client.id)}
              aria-pressed={isActive}
              className={cn(
                'flex items-center gap-2.5 rounded-lg border border-border px-3 py-2.5 text-left font-medium text-sm transition-colors',
                isActive
                  ? 'border-foreground/40 bg-background'
                  : 'border-border text-muted-foreground hover:bg-background/60 hover:text-foreground'
              )}
            >
              <McpClientLogo
                domain={client.logoDomain}
                name={client.name}
                className="size-5 shrink-0 rounded-lg"
              />
              <span className="truncate">{client.name}</span>
            </button>
          )
        })}
      </div>

      {/* Right: selected client's setup. `min-w-0` lets it hold the track width
          so the copyable command scrolls inside instead of widening the pane. */}
      <div className="min-w-0 rounded-lg border border-border bg-background p-5">
        <ClientSetup clientId={clientId} url={url} header />
      </div>
    </div>
  )
}

/**
 * "Connect your AI agent" panel — the clean MCP connection URL plus per-client
 * setup instructions (Claude Code, Cursor, Claude Desktop, VS Code, …) with a
 * ready-to-paste config. The URL is the same for brand and organization
 * credentials. Single source of truth shared by the onboarding wizard's
 * API/MCP step and the in-app settings surface; client recipes live in
 * `lib/mcp/clients.ts`, logos resolve via context.dev Logo Link
 * (`McpClientLogo`).
 */
export function McpConnectPanel({
  className,
  variant = 'card',
  hideConnectionUrl = false,
  url: fixedUrl,
}: McpConnectPanelProps) {
  const t = useTranslations('settings')
  const liveUrl = useMcpConnectionUrl()
  const url = fixedUrl ?? liveUrl
  const [clientId, setClientId] = useState<McpClientId>(DEFAULT_CLIENT)
  const isBare = variant === 'bare'

  if (variant === 'grid') {
    return <GridSetup url={url} className={className} />
  }

  return (
    <div
      className={cn(
        'flex flex-col',
        isBare
          ? 'gap-5'
          : 'gap-4 rounded-lg border border-border bg-muted/20 px-4 py-4',
        className
      )}
    >
      {!isBare ? (
        <div className="flex items-start gap-3">
          <Plug className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="flex flex-col gap-0.5">
            <p className="font-medium text-sm tracking-tight">
              {t('mcp.connect.cardTitle')}
            </p>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t('mcp.connect.cardDescription')}
            </p>
          </div>
        </div>
      ) : null}

      {hideConnectionUrl ? null : (
        <CopyField
          artifact={{ kind: 'url', language: 'text', build: (value) => value }}
          url={url}
        />
      )}

      <div className="flex flex-col gap-2.5">
        <McpClientPicker clientId={clientId} onSelect={setClientId} />

        <ClientSetup clientId={clientId} url={url} />
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <a
          href={MCP_DOCS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-muted-foreground text-sm underline underline-offset-2 transition-colors hover:text-foreground"
        >
          {t('mcp.connect.allClientsDocs')}
          <ArrowUpRight className="size-3.5" />
        </a>
        <a
          href={MCP_MARKETING_URL}
          className="inline-flex items-center gap-1 text-muted-foreground text-sm underline underline-offset-2 transition-colors hover:text-foreground"
        >
          {t('mcp.connect.whatYouCanBuild')}
          <ArrowUpRight className="size-3.5" />
        </a>
      </div>
    </div>
  )
}
